import { ProductPageProvider } from "@/components";
import { analyzeThemeColor, resolveSecondaryColor } from "@tripbox/ui";
import { PORTAL_DEFAULT_PRIMARY, PORTAL_DEFAULT_SECONDARY } from "../lib/theme";
import { Alert, App as AntApp, Button, Drawer, Dropdown, Form, Input, Modal, Result, Spin } from "antd";
import axios from "axios";
import {
  CalendarCheckIcon,
  ChevronDownIcon,
  FileTextIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  MenuIcon,
  PlusIcon,
  SparklesIcon,
  UserRoundIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { usePortalAiStatusQuery } from "../actions/assistant.actions";
import { Outlet, useLocation, useNavigate } from "react-router";
import { logoutPortalSession, portalRequest, refreshPortalSession, setPortalSession } from "../lib/auth";
import { getPortalInitials } from "../lib/branding";
import { getPortalLandingPath, getPortalPaths } from "../lib/routes";
import { useTenant } from "../tenant/TenantContext";
import { PortalAccountMenu } from "./PortalAccountMenu";
import { PortalBrand } from "./PortalProduct";
import { PortalSidebar, portalDropdownClassName, type PortalNavItem, type PortalSidebarProps } from "./PortalSidebar";

export type PortalSessionActor = {
  id: string;
  fullname: string;
  email: string;
  role: string;
  mustChangePassword?: boolean;
  agency?: { id: string; name: string };
};

export type PortalWorkspaceContext = {
  actorType: "tenant_user" | "agency_user";
  actor: PortalSessionActor;
  organisationName: string;
  organisationImage?: string | null;
  signOut: () => void;
};

type PortalSessionResponse = {
  actorType: "tenant_user" | "agency_user";
  actor: PortalSessionActor;
  organisation?: {
    name: string;
    image?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
  } | null;
  portal?: { slug: string; domain?: string | null } | null;
};

type PortalTenantOption = {
  slug: string;
  name: string;
  image?: string | null;
  domain?: string | null;
  current: boolean;
};

type PortalSwitchResponse = PortalSessionResponse & {
  token: string;
};

const getPortalError = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError<{ error?: string }>(error)) return fallback;
  return error.response?.data?.error || fallback;
};

type TenantTeamMemberNavbarProps = {
  actor: PortalSessionActor;
  organisationName: string;
  organisationImage?: string | null;
  onSignOut: () => void;
};

/** Renders the minimal portal navigation available to tenant team members. */
const TenantTeamMemberNavbar = ({
  actor,
  organisationName,
  organisationImage,
  onSignOut,
}: TenantTeamMemberNavbarProps) => (
  <header className="fixed inset-x-0 top-0 z-50 border-b border-gray-200 bg-white text-[#131B2A] shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
    <nav aria-label="Team member navigation">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <PortalBrand name={organisationName} image={organisationImage} compact navbar />

          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            rootClassName={portalDropdownClassName}
            menu={{
              items: [
                {
                  key: "profile",
                  icon: <UserRoundIcon size={15} />,
                  disabled: true,
                  label: (
                    <div className="min-w-56 py-1">
                      <p className="text-sm font-semibold text-[#131B2A]">{actor.fullname}</p>
                      <p className="mt-0.5 text-xs text-gray-500">{actor.email}</p>
                    </div>
                  ),
                },
                { type: "divider" },
                {
                  key: "logout",
                  icon: <LogOutIcon size={15} />,
                  danger: true,
                  label: "Sign out",
                  onClick: onSignOut,
                },
              ],
            }}
          >
            <button
              type="button"
              className="group flex h-11 shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white p-1.5 pr-2 text-sm transition-[border-color,background-color,transform] duration-150 hover:border-(--brand-primary)/50 hover:bg-[#FBFBFB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50 active:scale-[0.98]"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-(--brand-secondary) text-xs font-semibold tracking-tight text-white">
                {getPortalInitials(actor.fullname)}
              </span>
              <span className="hidden max-w-32 truncate text-left text-xs font-semibold text-[#131B2A] sm:block">
                {actor.fullname}
              </span>
              <ChevronDownIcon className="hidden h-3.5 w-3.5 text-gray-400 sm:block" strokeWidth={1.8} />
            </button>
          </Dropdown>
        </div>
      </div>
    </nav>
  </header>
);

/** Renders the standard unavailable-page state beneath the team member navigation. */
const TenantTeamMemberUnavailablePage = () => (
  <main className="h-96 content-center text-center">
    <Result
      status="404"
      title="404"
      subTitle="Sorry, the page you visited does not exist."
      extra={
        <Button
          type="primary"
          onClick={() => {
            window.location.href = "/";
          }}
        >
          Back Home
        </Button>
      }
    />
  </main>
);

/** Renders the isolated agency portal shell and protects its nested workspace routes. */
const PortalWorkspaceLayout = () => {
  const tenant = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const [session, setSession] = useState<PortalSessionResponse | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "unauthorized">("loading");
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [tenantOptions, setTenantOptions] = useState<PortalTenantOption[]>([]);
  const [switchingTenantSlug, setSwitchingTenantSlug] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // The AI assistant is hidden from the menu only when AI is switched off for the agency itself.
  const aiStatus = usePortalAiStatusQuery(session?.actorType === "agency_user");
  const agencyAiEnabled = aiStatus.data?.aiEnabled !== false;
  const [form] = Form.useForm();
  const portalPaths = getPortalPaths();
  const requestedPrimaryColor =
    session?.organisation?.primaryColor || tenant.branding?.primaryColor || PORTAL_DEFAULT_PRIMARY;
  const resolvedSecondary = resolveSecondaryColor(
    session?.organisation?.secondaryColor || tenant.branding?.secondaryColor,
    PORTAL_DEFAULT_SECONDARY,
  );
  const primaryColorAnalysis = analyzeThemeColor(requestedPrimaryColor);
  const resolvedPrimaryColor = primaryColorAnalysis.isAppropriate ? primaryColorAnalysis.color! : PORTAL_DEFAULT_PRIMARY;
  const resolvedPrimaryForeground = primaryColorAnalysis.isAppropriate
    ? primaryColorAnalysis.preferredForeground
    : analyzeThemeColor(PORTAL_DEFAULT_PRIMARY).preferredForeground;

  useEffect(() => {
    document.documentElement.style.setProperty("--brand-secondary", resolvedSecondary.color);
    document.documentElement.style.setProperty("--brand-secondary-hover", resolvedSecondary.hover);
  }, [resolvedSecondary.color, resolvedSecondary.hover]);

  useEffect(() => {
    document.documentElement.style.setProperty("--brand-primary", resolvedPrimaryColor);
    document.documentElement.style.setProperty("--brand-primary-foreground", resolvedPrimaryForeground);
    return () => {
      const fallbackAnalysis = analyzeThemeColor(tenant.branding?.primaryColor || PORTAL_DEFAULT_PRIMARY);
      const fallbackColor = fallbackAnalysis.isAppropriate ? fallbackAnalysis.color! : PORTAL_DEFAULT_PRIMARY;
      const fallbackForeground = fallbackAnalysis.isAppropriate
        ? fallbackAnalysis.preferredForeground
        : analyzeThemeColor(PORTAL_DEFAULT_PRIMARY).preferredForeground;
      document.documentElement.style.setProperty("--brand-primary", fallbackColor);
      document.documentElement.style.setProperty("--brand-primary-foreground", fallbackForeground);
    };
  }, [resolvedPrimaryColor, resolvedPrimaryForeground, tenant.branding?.primaryColor]);

  useEffect(() => {
    refreshPortalSession()
      .then(() => portalRequest<PortalSessionResponse>({ url: "/api/agent-portal/session", method: "get" }))
      .then((response) => {
        const sessionSlug = response.data.portal?.slug;
        if (tenant.tenantSlug && sessionSlug !== tenant.tenantSlug) {
          setState("unauthorized");
          return;
        }
        setSession(response.data);
        setMustChangePassword(Boolean(response.data.actor.mustChangePassword));
        setState("ready");
      })
      .catch(() => setState("unauthorized"));
  }, [tenant.tenantSlug]);

  useEffect(() => {
    if (session?.actorType !== "agency_user") return;
    portalRequest<{ tenants: PortalTenantOption[] }>({
      url: "/api/agent-portal/tenants",
      method: "get",
    })
      .then((response) => setTenantOptions(response.data.tenants))
      .catch(() => setTenantOptions([]));
  }, [session?.actor.id, session?.actorType, session?.portal?.slug]);

  const signOut = async () => {
    await logoutPortalSession();
    navigate(portalPaths.login);
  };

  const switchTenant = async (target: PortalTenantOption) => {
    const targetSlug = target.slug;
    if (targetSlug === session?.portal?.slug || switchingTenantSlug) return;
    if (tenant.tenantSlug) {
      if (!target.domain) {
        message.error("This workspace does not have a portal address configured");
        return;
      }
      setSwitchingTenantSlug(targetSlug);
      try {
        await logoutPortalSession();
      } finally {
        window.location.assign(`${window.location.protocol}//${target.domain}/login`);
      }
      return;
    }
    try {
      setSwitchingTenantSlug(targetSlug);
      const response = await portalRequest<PortalSwitchResponse>({
        url: "/api/agent-portal/auth/switch-tenant",
        method: "post",
        data: { tenantSlug: targetSlug },
      });
      setPortalSession(response.data.token, response.data.actor);
      setSession(response.data);
      setMustChangePassword(Boolean(response.data.actor.mustChangePassword));
      setTenantOptions((options) =>
        options.map((option) => ({ ...option, current: option.slug === response.data.portal?.slug })),
      );
      message.success(`Switched to ${response.data.organisation?.name || "partner workspace"}`);
      navigate(getPortalLandingPath(response.data.actorType));
    } catch (error: unknown) {
      message.error(getPortalError(error, "Unable to switch workspace"));
    } finally {
      setSwitchingTenantSlug(null);
    }
  };

  if (state === "loading") {
    return (
      <ProductPageProvider primaryColor={resolvedPrimaryColor} secondaryColor={resolvedSecondary.color}>
        <main className="flex min-h-[100dvh] items-center justify-center bg-[#FBFBFB]">
          <Spin size="large" />
        </main>
      </ProductPageProvider>
    );
  }

  if (state === "unauthorized" || !session) {
    return (
      <ProductPageProvider primaryColor={resolvedPrimaryColor} secondaryColor={resolvedSecondary.color}>
        <main className="flex min-h-[100dvh] items-center justify-center bg-[#FBFBFB] p-6">
          <div className="w-full max-w-lg overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_12px_32px_rgba(19,27,42,0.08)]">
            <Result
              status="403"
              title="Sign in required"
              subTitle="Your partner portal session has ended. Sign in again to continue."
              extra={
                <Button type="primary" onClick={() => navigate("/login")}>
                  Sign in
                </Button>
              }
            />
          </div>
        </main>
      </ProductPageProvider>
    );
  }

  const organisationName = session.organisation?.name || "Partner portal";

  if (session.actorType !== "agency_user") {
    return (
      <ProductPageProvider primaryColor={resolvedPrimaryColor} secondaryColor={resolvedSecondary.color}>
        <div className="min-h-[100dvh] bg-(--portal-canvas) text-[#131B2A]">
          <TenantTeamMemberNavbar
            actor={session.actor}
            organisationName={organisationName}
            organisationImage={session.organisation?.image}
            onSignOut={signOut}
          />
          <div className="mt-16 pb-6">
            {location.pathname === portalPaths.teamMemberHome ? (
              <Outlet
                context={
                  {
                    actorType: session.actorType,
                    actor: session.actor,
                    organisationName,
                    organisationImage: session.organisation?.image,
                    signOut,
                  } satisfies PortalWorkspaceContext
                }
              />
            ) : (
              <TenantTeamMemberUnavailablePage />
            )}
          </div>
        </div>
      </ProductPageProvider>
    );
  }

  const navItems: PortalNavItem[] = [
    { label: "Dashboard", path: portalPaths.dashboard, icon: LayoutDashboardIcon, exact: true },
    ...(agencyAiEnabled
      ? [{ label: "AI assistant", path: portalPaths.assistant, icon: SparklesIcon, exact: false }]
      : []),
    { label: "New trip", path: portalPaths.newRequest, icon: PlusIcon, exact: true },
    { label: "Quotes", path: portalPaths.itineraries, icon: FileTextIcon, exact: false },
    { label: "Bookings", path: portalPaths.bookings, icon: CalendarCheckIcon, exact: false },
  ];
  const isNavItemActive = (path: string, exact: boolean) => {
    return exact ? location.pathname === path : location.pathname.startsWith(path);
  };
  const availableTenants = tenantOptions.length
    ? tenantOptions
    : session.portal
      ? [
          {
            slug: session.portal.slug,
            name: organisationName,
            image: session.organisation?.image,
            domain: session.portal.domain,
            current: true,
          },
        ]
      : [];
  const sidebarProps: PortalSidebarProps = {
    navItems,
    isItemActive: isNavItemActive,
    organisationName,
    organisationImage: session.organisation?.image,
    tenants: availableTenants.map(({ slug, name, current }) => ({ slug, name, current })),
    switchingTenantSlug,
    onSwitchTenant: (slug) => {
      const target = availableTenants.find((option) => option.slug === slug);
      if (target) void switchTenant(target);
    },
  };
  const accountMenu = (
    <PortalAccountMenu
      actorName={session.actor.fullname}
      actorEmail={session.actor.email}
      agencyName={session.actor.agency?.name}
      profilePath={portalPaths.profile}
      onSignOut={signOut}
    />
  );

  return (
    <ProductPageProvider primaryColor={resolvedPrimaryColor} secondaryColor={resolvedSecondary.color}>
      <div className="min-h-[100dvh] bg-(--portal-canvas) text-[#131B2A]">
        <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-gray-200 lg:block">
          <PortalSidebar {...sidebarProps} />
        </aside>

        <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-3 border-b border-gray-200 bg-white px-4 shadow-[0_1px_2px_rgba(19,27,42,0.04)] sm:px-6 lg:hidden">
          <button
            type="button"
            aria-label="Open portal navigation"
            onClick={() => setMobileNavOpen(true)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-[#131B2A] transition-colors hover:border-(--brand-primary)/50 hover:bg-[#FBFBFB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50"
          >
            <MenuIcon className="h-5 w-5" strokeWidth={1.8} />
          </button>
          <div className="min-w-0 flex-1">
            <PortalBrand name={organisationName} image={session.organisation?.image} compact navbar />
          </div>
          {accountMenu}
        </header>

        <Drawer
          open={mobileNavOpen}
          placement="left"
          width={288}
          closable={false}
          onClose={() => setMobileNavOpen(false)}
          styles={{ body: { padding: 0 } }}
          aria-label="Portal navigation"
        >
          <PortalSidebar {...sidebarProps} onNavigate={() => setMobileNavOpen(false)} />
        </Drawer>

        <div className="mt-14 pb-6 lg:ml-64 lg:mt-0">
          <Outlet
            context={
              {
                actorType: session.actorType,
                actor: session.actor,
                organisationName,
                organisationImage: session.organisation?.image,
                signOut,
              } satisfies PortalWorkspaceContext
            }
          />
        </div>

        <Modal
          title="Change your temporary password"
          open={mustChangePassword}
          closable={false}
          maskClosable={false}
          footer={null}
        >
          <p className="mb-4 text-sm leading-6 text-gray-600">
            Choose a password known only to you before creating itineraries.
          </p>
          {changeError ? <Alert className="mb-4" type="error" showIcon message={changeError} /> : null}
          <Form
            form={form}
            layout="vertical"
            onFinish={async ({ newPassword, confirmPassword }) => {
              if (newPassword !== confirmPassword) {
                setChangeError("Passwords do not match");
                return;
              }
              try {
                setChangingPassword(true);
                setChangeError(null);
                await portalRequest({
                  url: "/api/agent-portal/auth/change-password",
                  method: "post",
                  data: { newPassword },
                });
                form.resetFields();
                setMustChangePassword(false);
                setTenantOptions((options) => options.filter((option) => option.current));
                message.success("Password updated. Other portal sessions have been signed out.");
              } catch (error: unknown) {
                setChangeError(getPortalError(error, "Unable to update password"));
              } finally {
                setChangingPassword(false);
              }
            }}
          >
            <Form.Item
              label="New password"
              name="newPassword"
              rules={[{ required: true, min: 8, message: "Use at least 8 characters" }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              label="Confirm new password"
              name="confirmPassword"
              rules={[{ required: true, message: "Confirm your new password" }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={changingPassword}>
              Update password
            </Button>
          </Form>
        </Modal>
      </div>
    </ProductPageProvider>
  );
};

export default PortalWorkspaceLayout;
